import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';

import { KeycloakSessionService } from '../services/auth/keycloak-session.service';

export interface RealmPolicies {
  id: string;
  realm: string;
  accessTokenLifespan: number;
  ssoSessionIdleTimeout: number;
  ssoSessionMaxLifespan: number;
  actionTokenGeneratedByUserLifespan: number;
  actionTokenGeneratedByAdminLifespan: number;
  bruteForceProtected: boolean;
  failureFactor: number;
  waitIncrementSeconds: number;
  maxFailureWaitSeconds: number;
  maxDeltaTimeSeconds: number;
  passwordPolicy: string;
  [key: string]: unknown;
}

export interface AuthenticationExecution {
  id: string;
  priority: number;
  providerId?: string;
  authenticationConfig?: string;
  requirement: string;
}

export interface EmailCodePolicy {
  id: string;
  alias: string;
  config: Record<string, string>;
}

@Injectable({ providedIn: 'root' })
export class IamPoliciesApiService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(KeycloakSessionService);

  realm() {
    return this.http.get<RealmPolicies>(this.session.adminUrl);
  }

  // Keycloak accepts a partial representation. Sending the entire old realm
  // would overwrite settings changed in another panel since it was opened.
  updateRealm(changes: Partial<RealmPolicies>) {
    return this.http.put<void>(this.session.adminUrl, changes);
  }

  ipPolicy() {
    return this.http.get<{ defaultAllowedIpNetworks: string[] }>(
      `${this.session.iamUrl}/ip-policy`,
    );
  }

  updateIpPolicy(defaultAllowedIpNetworks: string[]) {
    return this.http.post(`${this.session.iamUrl}/ip-policy`, {
      defaultAllowedIpNetworks,
    });
  }

  temporaryPasswordPolicy() {
    return this.http.get<{ lifespanSeconds: number }>(
      `${this.session.iamUrl}/temporary-password-policy`,
    );
  }

  updateTemporaryPasswordPolicy(lifespanSeconds: number) {
    return this.http.post(`${this.session.iamUrl}/temporary-password-policy`, {
      lifespanSeconds,
    });
  }

  executions() {
    return this.http.get<AuthenticationExecution[]>(
      `${this.session.adminUrl}/authentication/flows/mt-browser/executions`,
    );
  }

  updateExecution(execution: AuthenticationExecution, enabled: boolean) {
    if (!Number.isInteger(execution.priority)) {
      return throwError(
        () =>
          new Error(
            'Не удалось определить порядок шагов входа. Обновите настройки.',
          ),
      );
    }
    return this.http
      .put(
        `${this.session.adminUrl}/authentication/flows/mt-browser/executions`,
        {
          id: execution.id,
          priority: execution.priority,
          requirement: enabled ? 'REQUIRED' : 'DISABLED',
        },
      )
      .pipe(
        catchError((failure: unknown) => {
          const payload: unknown =
            failure instanceof HttpErrorResponse ? failure.error : null;
          const code =
            payload && typeof payload === 'object' && 'error' in payload
              ? payload.error
              : null;
          const messages: Record<string, string> = {
            email_otp_flow_invalid:
              'Настройки шагов входа некорректны. Обратитесь к администратору.',
            email_otp_flow_order_invalid:
              'Второй фактор должен выполняться после проверки логина и IP. Обратитесь к администратору.',
            email_otp_notifications_not_configured:
              'Доставка кодов не настроена. Двухфакторная авторизация не включена.',
            email_otp_delivery_unavailable:
              'Доставка кодов сейчас недоступна. Проверьте сервис уведомлений и повторите включение.',
          };
          return throwError(() =>
            typeof code === 'string' && messages[code]
              ? new Error(messages[code])
              : failure,
          );
        }),
      );
  }

  emailCodePolicy(id: string) {
    return this.http.get<EmailCodePolicy>(
      `${this.session.adminUrl}/authentication/config/${encodeURIComponent(id)}`,
    );
  }

  updateEmailCodePolicy(policy: EmailCodePolicy) {
    return this.http.put(
      `${this.session.adminUrl}/authentication/config/${encodeURIComponent(policy.id)}`,
      policy,
    );
  }
}
